import { Typography, makeStyles } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { withStyles } from "@material-ui/styles";
import cns from "classnames";
import sv from "date-fns/locale/sv";
import { transparentize } from "polished";
import { useState } from "react";
import DateInput, {
  CalendarContainerProps,
  ReactDatePickerCustomHeaderProps,
  ReactDatePickerProps,
  registerLocale
} from "react-datepicker6";
import "react-datepicker6/dist/react-datepicker.css";
import { ArrowDropDownIcon, CalendarTodayIcon } from "../../icons";
import { Color } from "../../theme/color";
import Button from "./button";

type Props = {
  date: Date | null;
  setDate: (d: Date | null) => void;
  label: string;
};

type PickerType = "month" | "year";

function CustomContainer({ className, children }: CalendarContainerProps) {
  const classes = useStyles({});

  return <div className={cns(className, classes.container)}>{children}</div>;
}

function CustomHeader(
  props: ReactDatePickerCustomHeaderProps & {
    currentPicker: PickerType;
    setCurrentPicker: (picker: PickerType) => void;
    currentMonth?: number;
  }
) {
  const { currentPicker, setCurrentPicker, currentMonth, ...params } = props;
  const classes = useStyles({});

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Maj",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dec"
  ];

  return (
    <div className={classes.headerContainer}>
      <Button
        className={classes.button}
        variant="text"
        onClick={() => setCurrentPicker("month")}
        style={{
          borderRadius: "0.5rem 0 0 0",
          paddingLeft: "1.5rem"
        }}
      >
        <Typography
          variant="h5"
          style={{
            color:
              currentPicker === "month"
                ? Color.PrimaryMain
                : transparentize(0.62, Color.DetailsInput)
          }}
        >
          {months[currentMonth ?? params.date.getMonth()]}
        </Typography>
        <ArrowDropDownIcon
          htmlColor={Color.PrimaryMain}
          visibility={currentPicker === "month" ? "visible" : "hidden"}
        />
      </Button>
      <Button
        className={classes.button}
        variant="text"
        onClick={() => setCurrentPicker("year")}
        style={{
          borderRadius: "0 0.5rem 0 0",
          paddingRight: "1.5rem"
        }}
      >
        <Typography
          variant="h5"
          style={{
            color:
              currentPicker === "year"
                ? Color.PrimaryMain
                : transparentize(0.62, Color.DetailsInput)
          }}
        >
          {params.date.getFullYear()}
        </Typography>
        <ArrowDropDownIcon
          htmlColor={Color.PrimaryMain}
          visibility={currentPicker === "year" ? "visible" : "hidden"}
        />
      </Button>
    </div>
  );
}

function RenderYearContent({
  year,
  selectedDate
}: {
  year: number;
  selectedDate: Date | null;
}) {
  const classes = useStyles({});
  const selected = selectedDate && selectedDate.getFullYear() === year;
  return (
    <span className={classes.valueWrapper}>
      <CheckIcon
        htmlColor={Color.PrimaryMain}
        style={{ visibility: selected ? "visible" : "hidden" }}
      />
      {year}
    </span>
  );
}

function RenderMonthContent({
  monthIndex,
  shortMonthText,
  selectedDate
}: {
  monthIndex: number;
  shortMonthText: string;
  selectedDate: Date | null;
}) {
  const classes = useStyles({});
  const selected = selectedDate && selectedDate.getMonth() === monthIndex;
  return (
    <span className={classes.valueWrapper}>
      <CheckIcon
        htmlColor={Color.PrimaryMain}
        style={{ visibility: selected ? "visible" : "hidden" }}
      />
      {shortMonthText.slice(0, 3)}
    </span>
  );
}

export function MonthYearPicker(props: Props & ReactDatePickerProps) {
  const classes = useStyles({});
  registerLocale("sv", sv);

  const [currentPicker, setCurrentPicker] = useState<PickerType>("year");

  const onChange = (date: Date | null) => {
    if (!date) {
      props.setDate(null);
      return;
    }
    if (currentPicker === "month") {
      const newDate = props.date ? props.date : date;
      newDate.setMonth(date.getMonth());
      props.setDate(newDate);
    } else if (currentPicker === "year") {
      const newDate = props.date ? props.date : date;
      newDate.setFullYear(date.getFullYear());
      props.setDate(newDate);
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="body1">{props.label}</Typography>
      <DateInput
        {...props}
        showIcon
        toggleCalendarOnIconClick
        icon={<CalendarTodayIcon className={classes.icon} />}
        className={classes.picker}
        selected={props.date}
        onChange={onChange}
        dateFormat={"yyyy-MM"}
        calendarContainer={CustomContainer}
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
        locale="sv"
        renderCustomHeader={(params: ReactDatePickerCustomHeaderProps) => {
          return (
            <CustomHeader
              {...params}
              currentPicker={currentPicker}
              setCurrentPicker={setCurrentPicker}
              currentMonth={props.date?.getMonth()}
            />
          );
        }}
        showMonthYearPicker={currentPicker === "month"}
        showYearPicker={currentPicker === "year"}
        shouldCloseOnSelect={false}
        disabledKeyboardNavigation={true}
        renderYearContent={(year) => (
          <RenderYearContent year={year} selectedDate={props.date} />
        )}
        renderMonthContent={(monthIndex, shortMonthText, _) => (
          <RenderMonthContent
            monthIndex={monthIndex}
            shortMonthText={shortMonthText}
            selectedDate={props.date}
          />
        )}
        yearItemNumber={10}
      />
      <GlobalCss />
    </div>
  );
}

const GlobalCss = withStyles(() => ({
  "@global": {
    ".react-datepicker__input-container": {
      position: "relative"
    },
    ".react-datepicker__header": {
      borderBottom: "none !important",
      padding: "0 !important",
      borderRadius: "0.5rem 0.5rem 0 0 !important",
      backgroundColor: `${transparentize(0.85, Color.PrimaryMain)} !important`
    },
    ".react-datepicker__view-calendar-icon input": {
      paddingLeft: "12px"
    },
    ".react-datepicker__month, .react-datepicker__year": {
      maxHeight: "200px",
      overflowY: "auto",
      margin: 0,
      padding: 0
    },
    ".react-datepicker__month .react-datepicker__month-text, .react-datepicker__year .react-datepicker__year-text":
      {
        height: "3rem",
        width: "100%",
        margin: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 0
      },

    ".react-datepicker__month-wrapper, .react-datepicker__year-wrapper": {
      display: "flex",
      flexDirection: "column",
      flexWrap: "nowrap"
    },

    ".react-datepicker__month-text, .react-datepicker__year-text": {
      "&:hover": {
        backgroundColor: transparentize(0.95, Color.PrimaryMain)
      }
    },

    ".react-datepicker__month-text--selected, .react-datepicker__year-text--selected, .react-datepicker__year-text--keyboard-selected, .react-datepicker__month-text--keyboard-selected":
      {
        color: Color.Body,
        backgroundColor: transparentize(0.85, Color.PrimaryMain),
        "&:hover": {
          backgroundColor: transparentize(0.85, Color.PrimaryMain)
        }
      }
  }
}))(() => null);

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    backgroundColor: "transparent"
  },
  wrapper: {
    display: "flex",
    flexDirection: "row"
  },
  button: {
    width: "6rem",
    boxShadow: "unset"
  },

  picker: {
    height: "2.5rem",
    borderRadius: "8px",
    border: `1px solid ${Color.LightGray}`,
    backgroundColor: "transparent",
    width: "100%",

    "&:focus": {
      outlineColor: Color.PrimaryMain
    }
  },
  icon: {
    position: "absolute",
    right: 0
  },
  container: {
    backgroundColor: Color.White,
    border: "none !important",
    boxShadow: `1px 6px 24px 0px ${Color.BoxShadow}`,
    borderRadius: "0.5rem !important"
  },
  headerContainer: {
    backgroundColor: transparentize(0.85, Color.PrimaryMain),
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    alignItems: "center",
    height: "100%",
    borderRadius: "0.5rem 0.5rem 0 0"
  },
  valueWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: "1rem",
    alignItems: "center",
    justifyContent: "start",
    width: "100%",
    paddingLeft: "1rem",
    textTransform: "capitalize"
  }
}));
